'use client';

import Link from 'next/link';
import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Image from 'next/image';
import CartButton from './CartButton';
import CartSidebar from './CartSidebar';
import NotificationBell from './NotificationBell';
import {
	Menu,
	X,
	User,
	LogOut,
	LayoutDashboard,
	ShoppingBag,
	House,
	Bike,
} from 'lucide-react';
import { FaShippingFast } from 'react-icons/fa';

export default function Navbar() {
	const { user, logout } = useAuth();
	const router = useRouter();
	const [open, setOpen] = useState(false); // Mobile menu state
	const [menuOpen, setMenuOpen] = useState(false); // User dropdown state
	const [isScrolled, setIsScrolled] = useState(false);
	const menuRef = useRef<HTMLDivElement | null>(null);

	const colors = {
		primary: '#051449',
		accent: '#FFCF23',
	};

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (
				menuRef.current &&
				!menuRef.current.contains(e.target as Node)
			) {
				setMenuOpen(false);
			}
		}
		function handleEscape(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				setMenuOpen(false);
				setOpen(false);
			}
		}
		function handleScroll() {
			setIsScrolled(window.scrollY > 10);
		}

		document.addEventListener('click', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		window.addEventListener('scroll', handleScroll);

		return () => {
			document.removeEventListener(
				'click',
				handleClickOutside,
			);
			document.removeEventListener('keydown', handleEscape);
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const handleLogout = async () => {
		try {
			await logout();
			router.push('/');
		} catch {
			router.push('/auth/login');
		}
	};

	return (
		<>
			{user?.role !== 'admin' && <CartSidebar />}

			<header
				className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
					isScrolled
						? 'bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm'
						: 'bg-white border-b border-gray-100'
				}`}
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between items-center h-16">
						{/* --- Brand --- */}
						<Link
							href="/"
							className="flex items-center gap-3 group"
						>
							<Image
								src={'/logo.jpg'}
								alt="Yunimall logo"
								width={40}
								height={40}
								className="rounded-lg"
								style={{ objectFit: 'cover' }}
							/>
							<div className="hidden sm:block">
								<p
									className="text-lg font-bold leading-tight"
									style={{ color: colors.primary }}
								>
									Yunimall
								</p>
								<p className="text-[10px] text-gray-500 font-medium tracking-wide">
									Connect. Buy. Sell.
								</p>
							</div>
						</Link>

						{/* --- Desktop Nav --- */}
						<nav className="hidden md:flex items-center gap-6">
							<Link
								href="/stores"
								className="text-sm font-medium text-gray-600 hover:text-[#051449] transition-colors"
							>
								Stores
							</Link>

							<Link
								href="/products"
								className="text-sm font-medium text-gray-600 hover:text-[#051449] transition-colors"
							>
								Products
							</Link>

							{user && (
								<Link
									href="/orders"
									className="text-sm font-medium text-gray-600 hover:text-[#051449] transition-colors"
								>
									My Orders
								</Link>
							)}

							{/* Divider */}
							<div className="h-6 w-px bg-gray-200"></div>

							{/* Cart Button (Hidden for Admins) */}
							{user?.role !== 'admin' && <CartButton />}

							{/* Notifications */}
							{user && <NotificationBell />}

							{/* Auth Actions */}
							{!user ? (
								<div className="flex items-center gap-3">
									<Link
										href="/auth/login"
										className="text-sm font-medium text-gray-700 hover:text-[#051449]"
									>
										Log in
									</Link>
									<Link
										href="/auth/register"
										className="text-sm font-semibold px-4 py-2 rounded-lg transition-transform hover:scale-105"
										style={{
											background: colors.accent,
											color: colors.primary,
										}}
									>
										Get Started
									</Link>
								</div>
							) : (
								<div className="relative" ref={menuRef}>
									<button
										onClick={() => setMenuOpen((s) => !s)}
										className="flex items-center gap-2 p-1 pr-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors"
									>
										<div
											className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
												user.isAlumni
													? 'bg-[#FFCF23] text-[#051449]'
													: 'bg-[#051449] text-white'
											}`}
										>
											{user.name?.[0]?.toUpperCase()}
										</div>
										<span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">
											{user.name?.split(' ')[0]}
										</span>
									</button>

									{/* Dropdown Menu */}
									{menuOpen && (
										<div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
											<div className="px-4 py-2 border-b border-gray-100 mb-1">
												<p className="text-sm font-semibold text-gray-900 truncate">
													{user.name}
												</p>
												<p className="text-xs text-gray-500 truncate">
													{user.email}
												</p>
											</div>

											{user.role === 'vendor' && (
												<Link
													href="/vendor/dashboard"
													className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
													onClick={() => setMenuOpen(false)}
												>
													<LayoutDashboard className="w-4 h-4 mr-2" />
													Store Dashboard
												</Link>
											)}

												{(user.role === 'admin' || user.role === 'manager') && (
												<Link
													href="/admin/dashboard"
													className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
													onClick={() => setMenuOpen(false)}
												>
													<LayoutDashboard className="w-4 h-4 mr-2" />
													Admin Console
												</Link>
											)}

											{user.role === 'admin' && (
												<Link
													href="/admin/shipping"
													className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
													onClick={() => setMenuOpen(false)}
												>
													<FaShippingFast className="w-4 h-4 mr-2" />
													Shipping
												</Link>
											)}

												<Link
													href="/"
													className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
													onClick={() => setMenuOpen(false)}
												>
													<House className="w-4 h-4 mr-2" />
													Home
												</Link>
											<Link
												href="/orders"
												className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
												onClick={() => setMenuOpen(false)}
											>
												<ShoppingBag className="w-4 h-4 mr-2" />
												My Orders
											</Link>

												<Link
													href="/track"
													className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
													onClick={() => setMenuOpen(false)}
												>
													<FaShippingFast className="w-4 h-4 mr-2" />
													Order Tracking
												</Link>

											<Link
												href="/profile"
												className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
												onClick={() => setMenuOpen(false)}
											>
												<User className="w-4 h-4 mr-2" />
												Profile
											</Link>

									<Link
										href="/logistics/auth/login"
										className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
										onClick={() => setMenuOpen(false)}
									>
										<Bike className="w-4 h-4 mr-2" />
										Rider Portal
									</Link>

											<div className="border-t border-gray-100 mt-1 pt-1">
												<button
													onClick={handleLogout}
													className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
												>
													<LogOut className="w-4 h-4 mr-2" />
													Sign Out
												</button>
											</div>
										</div>
									)}
								</div>
							)}
						</nav>

						{/* --- Mobile Actions --- */}
						<div className="flex items-center gap-3 md:hidden">
							{user?.role !== 'admin' && <CartButton />}
							{user && <NotificationBell />}
							<button
								onClick={() => setOpen(!open)}
								className="p-2 rounded-lg text-gray-600 hover:bg-gray-100"
							>
								{open ? (
									<X className="w-6 h-6" />
								) : (
									<Menu className="w-6 h-6" />
								)}
							</button>
						</div>
					</div>
				</div>

				{/* --- Mobile Menu --- */}
				{open && (
					<div className="md:hidden border-t border-gray-100 bg-white">
						<div className="px-4 py-4 space-y-3">
							<Link
								href="/stores"
								onClick={() => setOpen(false)}
								className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
							>
								Stores
							</Link>
							<Link
								href="/products"
								onClick={() => setOpen(false)}
								className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
							>
								Products
							</Link>

							<div className="border-t border-gray-100 my-2 pt-2"></div>

							{!user ? (
								<div className="space-y-2">
									<Link
										href="/auth/login"
										onClick={() => setOpen(false)}
										className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
									>
										Log in
									</Link>
									<Link
										href="/auth/register"
										onClick={() => setOpen(false)}
										className="block px-3 py-2 rounded-md text-base font-bold text-[#051449] bg-[#FFCF23]"
									>
										Get Started
									</Link>
								</div>
							) : (
								<div className="space-y-2">
									<div className="px-3 py-2">
										<div className="font-semibold text-gray-900">
											{user.name}
										</div>
										<div className="text-sm text-gray-500">
											{user.email}
										</div>
									</div>

									{user.role === 'vendor' && (
										<Link
											href="/vendor/dashboard"
											onClick={() => setOpen(false)}
											className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
										>
											My Stores
										</Link>
									)}

									<Link
										href="/orders"
										onClick={() => setOpen(false)}
										className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
									>
										My Orders
									</Link>

							<Link
								href="/logistics/auth/login"
								onClick={() => setOpen(false)}
								className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
							>
								Rider Portal
							</Link>

										<Link
											href="/track"
											onClick={() => setOpen(false)}
											className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
										>
											Order Tracking
										</Link>

									<button
										onClick={() => {
											handleLogout();
											setOpen(false);
										}}
										className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50"
									>
										Sign Out
									</button>
								</div>
							)}
						</div>
					</div>
				)}
			</header>

			{/* Spacer to prevent content overlap with fixed header */}
			<div className="h-16"></div>
		</>
	);
}
